import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

// Components
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpTooltip, WpTooltipTrigger, WpTooltipContent } from 'Wpds/components/tooltip';
import EditTemplateModal from '../EditTemplateModal/EditTemplateModal';
import { WpModal, WpModalBody, WpModalFooter, WpModalHeader } from 'Wpds/components/modal';
import WpAlert from 'Wpds/components/alert/WpAlert';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';

// Selectors
import * as WorkspaceTemplatesSelectors from 'Shared/selectors/WorkspaceTemplates.selectors';

// Actions
import * as WorkspaceActions from 'Shared/actions/workspaces';
import * as WorkspaceTemplatesActions from 'Shared/actions/WorkspaceTemplates.actions';

// Utils
import { useToggle } from 'Shared/utils/hooks';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

//Styling
import './TemplatePreviewModal.scss';

type Props = {
  isInitialChoice: boolean;
  templateSlug: string;
  startWithPreview: boolean;
  onToggle(): void;
  onSubmit?: Function;
  onBackClick(): void;
};

function TemplatePreviewModal({
  isInitialChoice,
  templateSlug,
  onToggle,
  onSubmit,
  startWithPreview,
  onBackClick,
}: Props) {
  const dispatch = useDispatch();
  const [isLoading, toggleIsLoading] = useToggle(false);
  const [isEditing, toggleIsEditing] = useToggle(false);
  const [isConfirmModalOpen, isConfirmModalOpenToggle] = useToggle(false);
  const template = useSelector(state =>
    WorkspaceTemplatesSelectors.getWorkspaceTemplateBySlug(state, { slug: templateSlug })
  ).toJS();
  const workspaceId = useSelector(state => state.workspace.id);
  const { id: userId, isOrgAdmin } = useCurrentUser();
  const sourcePage = isInitialChoice ? 'choose_template_page' : 'workspace';

  const handleEditTemplate = () => {
    toggleIsEditing();
  };

  const handleSubmit = useCallback(async () => {
    toggleIsLoading();
    const response = await dispatch(WorkspaceActions.applyTemplateAction({ workspaceId, templateSlug, sourcePage }));
    if (response?.status === 200) {
      if (onSubmit) onSubmit();
    } else toggleIsLoading();
  }, [dispatch, workspaceId, templateSlug, onToggle, onSubmit]);

  const handleDeleteTemplate = () => {
    dispatch(WorkspaceTemplatesActions.deleteWorkspaceTemplate(template.slug)).then(() => {
      onToggle();
    });
  };

  const handleDeleteClick = () => {
    isConfirmModalOpenToggle();
  };

  const canEdit = template.isCustom && (template.userId == userId || isOrgAdmin);

  const baseClassName = 'TemplatePreviewModal';
  const footerClasses = classNames(
    `${baseClassName}__footer`,
    canEdit ? `${baseClassName}__footer-custom` : `${baseClassName}__footer`
  );

  const Topic = ({ discussionTopic }) => {
    const text = discussionTopic?.text === '' || discussionTopic?.text ? discussionTopic?.text : discussionTopic;
    const key =
      discussionTopic?.uuid ||
      text
        .replace(/[^a-zA-Z ]/g, '')
        .replace(/\s/g, '-')
        .toLowerCase();

    return (
      <>
        <div key={key} className={`${baseClassName}____section__discussionTopic d-flex mb-2`}>
          <div>
            <span>
              <WpIcon name="check-circle" color={ColorConstants.gray[600]} />
            </span>
            <span className="ml-2">{text}</span>
          </div>
          <div>
            {discussionTopic?.recurrence && (
              <WpTooltip>
                <WpTooltipTrigger>
                  <span className="ml-3">
                    <WpIcon name="recycle" />
                  </span>
                </WpTooltipTrigger>
                <WpTooltipContent>Recurring Topic</WpTooltipContent>
              </WpTooltip>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <WpModal isOpen onToggle={onToggle} hideBackdrop={!isInitialChoice} animation={isInitialChoice} size="md">
      <WpModalHeader>
        <div className={`${baseClassName}__editable-text`}>
          {template.name}
          {canEdit && (
            <div className={`${baseClassName}__editable-text__edit-icon`}>
              <WpButton variant="tertiary" onClick={handleEditTemplate}>
                <WpIcon name="pencil" size="sm" />
              </WpButton>
            </div>
          )}
        </div>
        {isEditing && <EditTemplateModal onToggle={toggleIsEditing} templateSlug={templateSlug} />}
      </WpModalHeader>
      <WpModalBody>
        <div className={baseClassName}>
          <div className="mb-2">
            <div className={`${baseClassName}__editable-text`}>
              {template.description}
              {canEdit && template.description && (
                <div className={`${baseClassName}__editable-text__edit-icon`}>
                  <WpButton variant="tertiary" onClick={handleEditTemplate}>
                    <WpIcon name="pencil" size="sm" />
                  </WpButton>
                </div>
              )}
            </div>
          </div>
          <WpAlert type="info">You will be able to edit and customize this template in your workspace</WpAlert>
          <div className={`${baseClassName}__sections mt-4`}>
            {template.sections.map(section => (
              <div key={section.uuid || section.name} className="p-3 mb-3">
                <div className={`${baseClassName}____sections__name d-flex justify-content-between`}>
                  <div>
                    <span>{section.name}</span>
                  </div>
                  <div>
                    {section.recurrence && (
                      <span className="ml-3">
                        <WpIcon name="recycle" />
                      </span>
                    )}
                  </div>
                </div>
                {section.description && (
                  <div className={`${baseClassName}__sections__description mb-3`}>{section.description}</div>
                )}
                {(section.discussionTopics || []).map(discussionTopic => (
                  <Topic discussionTopic={discussionTopic} />
                ))}
                {(section.discussionTopicsDraftJs || []).map(discussionTopic => (
                  <Topic discussionTopic={discussionTopic} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <div className={footerClasses}>
          {canEdit && (
            <WpButton variant="secondary" color="danger" onClick={handleDeleteClick}>
              <WpIcon name="trash" />
              Delete
            </WpButton>
          )}
          <WpButtonGroup>
            <WpButton variant="tertiary" onClick={onBackClick}>
              {startWithPreview ? 'See more templates' : 'Back'}
            </WpButton>
            <WpButton qaTest="confirm-invite-group-workspace-submit-button" loading={isLoading} onClick={handleSubmit}>
              Use Template
            </WpButton>
          </WpButtonGroup>
        </div>
      </WpModalFooter>
      {isConfirmModalOpen ? (
        <ConfirmModal
          isOpen
          onToggle={isConfirmModalOpenToggle}
          onConfirmClick={handleDeleteTemplate}
          confirmButtonLabel="Delete"
        >
          <div>
            <p>Are you sure you want to delete this template?</p>
            <WpAlert type="note">This won&apos;t affect any of your existing workspaces.</WpAlert>
          </div>
        </ConfirmModal>
      ) : null}
    </WpModal>
  );
}

export default TemplatePreviewModal;
